import type { ClimateZone } from '@/db/types';

/**
 * Average frost dates by location. Used to schedule keyed-event tasks
 * — winterize outdoor faucets, blow out irrigation, prep garden beds —
 * at the right LOCAL time for the user's home instead of a generic
 * national calendar.
 *
 * Two strategies in priority order:
 *   1. Latitude-based linear approximation (preferred). Works for any
 *      lat between ~20°N and ~55°N — the contiguous US plus most of
 *      Hawaii / lower Canada. Fast, deterministic, no table lookups.
 *   2. Climate-zone fallback. Used when the user hasn't shared their
 *      location yet (ZIP gave us a climate zone but not lat). Coarser
 *      but better than a flat calendar.
 *
 * These are AVERAGES — actual frost dates vary year to year by ±2
 * weeks. We schedule keyed tasks 1–4 weeks BEFORE the event to leave
 * buffer for early frosts (better safe than burst pipes).
 *
 * Data sources (informed estimates, no API call):
 *   - USDA hardiness zones + last/first frost averages
 *   - NOAA climate normals 1991–2020
 *
 * Future Tier 3 (out of scope today): a live weather-API hook so we
 * can fire "cover frost-sensitive plants tonight" when actual NWS
 * forecast hits 34°F within 48h.
 */

export interface FrostWindow {
  /** Average last spring frost as Julian day-of-year (1–365).
   *  Anchors "start of growing season" tasks. */
  lastFrostJulian: number;
  /** Average first fall frost as Julian day-of-year.
   *  Anchors "winterize before this date" tasks. */
  firstFrostJulian: number;
}

/**
 * Linear approximation calibrated against known cities:
 *
 *   Atlanta (lat 33.7): last_frost ≈ Julian 78 (Mar 19),
 *                       first_frost ≈ Julian 317 (Nov 13)
 *   Kansas City (lat 39.1): ~Julian 105 / 295
 *   Minneapolis (lat 44.9): ~Julian 135 / 270
 *   Burlington VT (lat 44.5): ~Julian 133 / 272
 *
 * Slopes derived to land within 1 week of the city averages above.
 */
function frostFromLat(lat: number): FrostWindow {
  return {
    lastFrostJulian: Math.round(-90 + 5 * lat),
    firstFrostJulian: Math.round(450 - 4 * lat),
  };
}

/** Per-climate-zone defaults used when the user hasn't shared lat. */
const ZONE_DEFAULTS: Record<ClimateZone, FrostWindow> = {
  'cold-humid':  { lastFrostJulian: 135, firstFrostJulian: 270 }, // ~MN/WI: May 15 / Sep 27
  'cold-dry':    { lastFrostJulian: 135, firstFrostJulian: 265 }, // ~CO/MT: May 15 / Sep 22
  'mixed-humid': { lastFrostJulian: 105, firstFrostJulian: 290 }, // ~KS/MO: Apr 15 / Oct 17
  'mixed-dry':   { lastFrostJulian: 95,  firstFrostJulian: 295 }, // ~AZ-mid: Apr 5 / Oct 22
  'hot-humid':   { lastFrostJulian: 60,  firstFrostJulian: 325 }, // ~GA/FL: Mar 1 / Nov 21
  'hot-dry':     { lastFrostJulian: 75,  firstFrostJulian: 315 }, // ~TX/AZ: Mar 16 / Nov 11
  marine:        { lastFrostJulian: 85,  firstFrostJulian: 310 }, // ~PNW coast: Mar 26 / Nov 6
  unknown:       { lastFrostJulian: 120, firstFrostJulian: 280 }, // US average-ish
};

/**
 * Get the frost window for a property. Lat takes priority — it's the
 * most accurate signal. Climate zone is the fallback.
 *
 * Returns null only when we have neither — but our schema always
 * gives us at least an "unknown" climate zone for any saved property,
 * so this is mostly defensive.
 */
export function frostWindowFor(args: {
  lat?: number | null;
  climateZone?: ClimateZone | null;
}): FrostWindow | null {
  if (
    typeof args.lat === 'number' &&
    args.lat >= 20 &&
    args.lat <= 55 &&
    Number.isFinite(args.lat)
  ) {
    return frostFromLat(args.lat);
  }
  if (args.climateZone) {
    return ZONE_DEFAULTS[args.climateZone] ?? ZONE_DEFAULTS.unknown;
  }
  return null;
}

/**
 * Materialize a Date for the given Julian day-of-year in a given
 * year. Handles wrap-around if julian > 365 (rare but possible after
 * adding an offset).
 */
export function dateFromJulian(julian: number, year: number): Date {
  const d = new Date(year, 0, 1);
  d.setDate(julian);
  return d;
}
